import {
  computeSuspectWindow,
  type ProblemTimelineParams,
  type TimelineMilestone,
  type TimelineMilestoneKind,
} from "@playbookai/widget-schemas";
import type { CategoricalTint } from "../../lib/tints.js";

export const DAY = 86_400_000;

/** Keep the same first authored anchors used by the exposure calculation. */
export function keyMilestones(
  milestones: readonly TimelineMilestone[],
): TimelineMilestone[] {
  if (milestones.length <= 4) return sortMilestones(milestones);
  const kinds: TimelineMilestoneKind[] = [
    "last_known_good",
    "occurred",
    "recognised",
    "containment_started",
  ];
  const selected = kinds.flatMap((kind) => {
    const match = milestones.find((item) => item.kind === kind);
    return match ? [match] : [];
  });
  for (const item of sortMilestones(milestones)) {
    if (selected.length >= 4) break;
    if (!selected.some((entry) => entry.id === item.id)) selected.push(item);
  }
  return sortMilestones(selected);
}

export const KIND_LABEL: Record<TimelineMilestoneKind, string> = {
  last_known_good: "Last known good",
  occurred: "Problem occurred",
  recognised: "First recognised",
  solving: "Solving started",
  containment_started: "Containment started",
  countermeasure: "Countermeasure live",
  custom: "Milestone",
};

/* Milestone kinds are CATEGORIES, not statuses, so they take the categorical
   tint ramp (ADR-0092) rather than the semantic green/red/amber hues. Fills and
   dots use all seven tints; label text uses the hue-family tint that clears AA
   on the dark theme (the 68%-lightness 3/5/7 steps). */
export const KIND_TONE: Record<TimelineMilestoneKind, string> = {
  last_known_good: "bg-pb-tint-5 border-pb-tint-5 text-pb-tint-5",
  occurred: "bg-pb-tint-2 border-pb-tint-2 text-pb-tint-3",
  recognised: "bg-pb-tint-7 border-pb-tint-7 text-pb-tint-7",
  solving: "bg-pb-tint-4 border-pb-tint-4 text-pb-tint-5",
  containment_started: "bg-pb-tint-6 border-pb-tint-6 text-pb-tint-7",
  countermeasure: "bg-pb-tint-1 border-pb-tint-1 text-pb-tint-7",
  custom: "bg-pb-tint-3 border-pb-tint-3 text-pb-tint-3",
};

export const KIND_TEXT: Record<TimelineMilestoneKind, string> = {
  last_known_good: "text-pb-tint-5",
  occurred: "text-pb-tint-3",
  recognised: "text-pb-tint-7",
  solving: "text-pb-tint-5",
  containment_started: "text-pb-tint-7",
  countermeasure: "text-pb-tint-7",
  custom: "text-pb-tint-3",
};

/** The milestone marker's categorical tint (StatusDot `tint`). */
export const KIND_TINT: Record<TimelineMilestoneKind, CategoricalTint> = {
  last_known_good: 5,
  occurred: 2,
  recognised: 7,
  solving: 4,
  containment_started: 6,
  countermeasure: 1,
  custom: 3,
};

/** The timeline track's hollow-marker ring colours (an estimated or projected
 *  date is not a fact yet). The track keeps today's status colours: OK-86's
 *  kind-tint remap covers the milestone list only, and no StatusDot tone or
 *  tint draws `pb-purple`. */
export const KIND_BORDER: Record<TimelineMilestoneKind, string> = {
  last_known_good: "bg-pb-card border-pb-green",
  occurred: "bg-pb-card border-pb-red",
  recognised: "bg-pb-card border-pb-amber",
  solving: "bg-pb-card border-pb-cyan",
  containment_started: "bg-pb-card border-pb-purple",
  countermeasure: "bg-pb-card border-pb-green",
  custom: "bg-pb-card border-pb-purple",
};

/** The marker's StatusDot look: a filled dot for a fact, a ring for an
 *  estimated date and a dashed ring for a projected one — an estimated or
 *  projected date is not a fact yet. */
export function markerDot(milestone: {
  readonly kind: TimelineMilestoneKind;
  readonly estimated?: boolean | undefined;
  readonly projected?: boolean | undefined;
}): {
  readonly tint: CategoricalTint;
  readonly variant: "solid" | "outline" | "dashed";
} {
  return {
    tint: KIND_TINT[milestone.kind],
    variant: milestone.projected
      ? "dashed"
      : milestone.estimated
        ? "outline"
        : "solid",
  };
}

/**
 * Chronological order with a total, stable tie-break. Milestones whose date is
 * unparseable sort last rather than scattering, so the ordered-cell track keeps
 * a deterministic column order for identical or missing dates.
 */
export function sortMilestones(
  milestones: readonly TimelineMilestone[],
): TimelineMilestone[] {
  return [...milestones]
    .map((milestone, index) => ({ milestone, index }))
    .sort((a, b) => {
      const aDate = dateMs(a.milestone.date);
      const bDate = dateMs(b.milestone.date);
      const aValid = Number.isFinite(aDate);
      const bValid = Number.isFinite(bDate);
      if (!aValid || !bValid) {
        if (aValid) return -1;
        if (bValid) return 1;
        return a.index - b.index;
      }
      return aDate - bDate || a.index - b.index;
    })
    .map((entry) => entry.milestone);
}

/**
 * Apply an explicit id order to `milestones`, immutably. Ids the caller did not
 * mention keep their relative order at the end, so a stale drag payload can
 * never drop a milestone. Always returns a newly allocated array.
 */
export function reorderMilestones(
  milestones: readonly TimelineMilestone[],
  orderedIds: readonly string[],
): TimelineMilestone[] {
  const remaining = new Map(
    milestones.map((milestone) => [milestone.id, milestone]),
  );
  const next: TimelineMilestone[] = [];
  for (const id of orderedIds) {
    const milestone = remaining.get(id);
    if (!milestone) continue;
    remaining.delete(id);
    next.push(milestone);
  }
  return [...next, ...remaining.values()];
}

/**
 * Move one milestone `delta` slots within the manual order. Returns the input
 * array by reference for a no-op so callers can skip an empty `onChange`.
 */
export function moveMilestone(
  milestones: readonly TimelineMilestone[],
  id: string,
  delta: number,
): readonly TimelineMilestone[] {
  const from = milestones.findIndex((milestone) => milestone.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= milestones.length || delta === 0) {
    return milestones;
  }
  const next = [...milestones];
  const [moved] = next.splice(from, 1);
  if (!moved) return milestones;
  next.splice(to, 0, moved);
  return next;
}

export interface CertaintyCounts {
  readonly confirmed: number;
  readonly estimated: number;
  readonly projected: number;
}

export function certaintyCounts(
  milestones: readonly TimelineMilestone[],
): CertaintyCounts {
  let estimated = 0;
  let projected = 0;
  for (const milestone of milestones) {
    if (milestone.projected) projected += 1;
    else if (milestone.estimated) estimated += 1;
  }
  return {
    confirmed: milestones.length - estimated - projected,
    estimated,
    projected,
  };
}

/** "3 confirmed · 1 estimated" — omits the zero buckets. */
export function certaintySummary(counts: CertaintyCounts): string {
  const parts: string[] = [];
  if (counts.confirmed > 0) parts.push(`${counts.confirmed} confirmed`);
  if (counts.estimated > 0) parts.push(`${counts.estimated} estimated`);
  if (counts.projected > 0) parts.push(`${counts.projected} projected`);
  return parts.length > 0 ? parts.join(" · ") : "No milestones";
}

export function certaintyLabel(milestone: TimelineMilestone): string {
  if (milestone.projected) return "Projected";
  if (milestone.estimated) return "Estimated";
  return "Confirmed";
}

/**
 * Column index range covered by the suspect window in the ordered-cell track.
 * Returns `null` when either anchor is missing or the pair is out of order —
 * the same conditions under which `timelineSummary` refuses to size a window.
 */
export function suspectWindowColumns(
  ordered: readonly TimelineMilestone[],
): { readonly start: number; readonly end: number } | null {
  const startIndex = ordered.findIndex(
    (item) => item.kind === "last_known_good",
  );
  const occurredIndex = ordered.findIndex((item) => item.kind === "occurred");
  const anchor = startIndex >= 0 ? startIndex : occurredIndex;
  const endIndex = ordered.findIndex((item) => item.kind === "recognised");
  if (anchor < 0 || endIndex < 0 || endIndex < anchor) return null;
  return { start: anchor, end: endIndex };
}

export function dateMs(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return Number.NaN;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date.getTime()
    : Number.NaN;
}

export function isoDate(value: number): string {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function shortDate(value: string): string {
  const parsed = dateMs(value);
  return Number.isNaN(parsed)
    ? value
    : new Date(parsed).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
}

export interface TimelineSummary {
  readonly days: number | null;
  readonly exposure: number | null;
  readonly exposureUnit: string;
  readonly issues: string[];
}

export function timelineSummary(data: ProblemTimelineParams): TimelineSummary {
  const suspectWindow = computeSuspectWindow(data);
  const lastKnownGood = data.milestones.find(
    (item) => item.kind === "last_known_good",
  );
  const occurred = data.milestones.find((item) => item.kind === "occurred");
  const recognised = data.milestones.find((item) => item.kind === "recognised");
  const start = lastKnownGood ?? occurred;
  const issues: string[] = [];
  if (!start) issues.push("No start date — exposure can't be sized yet.");
  if (!recognised) issues.push("No detection date yet.");
  if (!start || !recognised) {
    return {
      days: null,
      exposure: null,
      exposureUnit: suspectWindow.exposureUnit,
      issues,
    };
  }
  const occurredAt = dateMs(start.date);
  const recognisedAt = dateMs(recognised.date);
  if (!Number.isFinite(occurredAt) || !Number.isFinite(recognisedAt)) {
    issues.push("Enter valid dates to size the suspect window.");
    return {
      days: null,
      exposure: null,
      exposureUnit: suspectWindow.exposureUnit,
      issues,
    };
  }
  return {
    days: suspectWindow.durationDays,
    exposure: suspectWindow.exposure,
    exposureUnit: suspectWindow.exposureUnit,
    issues: [...suspectWindow.issues],
  };
}
